import React, { useState, Fragment } from 'react';
import { formatDate, getStaffSizeLabel, getAdminStatusLabel } from '../../utils/inseeCodeMappings';
import { MagnifyingGlassIcon, DocumentTextIcon, ClipboardIcon, MapPinIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

interface NAFInfo {
  code: string;
  intitule: string;
  intitule65?: string;
  intitule40?: string;
  niveau: string;
}

interface Etablissement {
  etablissementSiege: string;
  etatAdministratifEtablissement: string;
  libelleCommuneEtablissement: string;
  codePostalEtablissement: string;
  activitePrincipaleEtablissement: string;
  trancheEffectifsEtablissement: string;
  numeroVoieEtablissement: string;
  typeVoieEtablissement: string;
  libelleVoieEtablissement: string;
  complementAdresseEtablissement: string;
  dateCreationEtablissement: string;
  enseigne1Etablissement: string;
  denominationUsuelleEtablissement: string;
  nafInfo: NAFInfo | null;
}

interface EstablishmentSectionProps {
  siren: string;
  siret: string;
  etablissement: Etablissement | null;
  isLoading: boolean;
  description?: string | null;
  companyName?: string;
}

interface NDWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  actionType: 'map' | 'copy';
}

const NDWarningModal: React.FC<NDWarningModalProps> = ({ isOpen, onClose, onConfirm, actionType }) => {
  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                      Données limitées
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Certaines informations de cette entreprise sont marquées comme non divulguées [ND] dans les données ouvertes.
                        {actionType === 'map' 
                          ? " L'adresse est donc très minimaliste et peut ne pas être précise sur Google Maps." 
                          : " L'adresse copiée peut être incomplète."}
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        Nous ne pouvons pas fournir plus d'informations que celles déjà affichées.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto"
                    onClick={() => {
                      onClose();
                      onConfirm();
                    }}
                  >
                    Continuer quand même
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={onClose}
                  >
                    Annuler
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

const EstablishmentSection: React.FC<EstablishmentSectionProps> = ({ 
  siren,
  siret,
  etablissement, 
  isLoading,
  description,
  companyName
}) => {
  const [copyStatus, setCopyStatus] = useState<{[key: string]: boolean}>({});
  const [showNDWarning, setShowNDWarning] = useState<boolean>(false);
  const [ndWarningType, setNdWarningType] = useState<'map' | 'copy'>('map');
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  // Action buttons
  const openGoogleSearch = () => {
    // Filter out [ND] values
    const searchTerms = [
      companyName && companyName !== '[ND]' ? companyName : null,
      etablissement?.denominationUsuelleEtablissement && etablissement.denominationUsuelleEtablissement !== '[ND]' ? etablissement.denominationUsuelleEtablissement : null,
      etablissement?.libelleCommuneEtablissement && etablissement.libelleCommuneEtablissement !== '[ND]' ? etablissement.libelleCommuneEtablissement : null,
      etablissement?.codePostalEtablissement && etablissement.codePostalEtablissement !== '[ND]' ? etablissement.codePostalEtablissement : null,
      description && description !== '[ND]' ? description : null
    ].filter(Boolean);
    
    const searchQuery = searchTerms.join(' ').trim();
    const encodedQuery = encodeURIComponent(searchQuery);
    
    window.open(
      `https://www.google.com/search?q=${encodedQuery}`,
      '_blank'
    );
  };

  const openSirenPdf = () => {
    window.open(
      `https://api-avis-situation-sirene.insee.fr/identification/pdf/${siret}`,
      '_blank'
    );
  };

  const openInpiExtrait = () => {
    window.open(
      `https://data.inpi.fr/export/companies?format=pdf&ids=[%22${siren}%22]`,
      '_blank'
    );
  };

  // Handle copy to clipboard
  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopyStatus({ ...copyStatus, [type]: true });
        
        // Reset the copy status after 2 seconds
        setTimeout(() => {
          setCopyStatus({ ...copyStatus, [type]: false });
        }, 2000);
      },
      (err) => {
        console.error('Could not copy text: ', err);
      }
    );
  };

  // Get formatted address for copying
  const getFormattedAddress = () => {
    if (!etablissement) return '';
    
    const addressComponents = [
      etablissement.numeroVoieEtablissement !== '[ND]' ? etablissement.numeroVoieEtablissement : null,
      etablissement.typeVoieEtablissement !== '[ND]' ? etablissement.typeVoieEtablissement : null,
      etablissement.libelleVoieEtablissement !== '[ND]' ? etablissement.libelleVoieEtablissement : null,
      etablissement.complementAdresseEtablissement !== '[ND]' ? etablissement.complementAdresseEtablissement : null,
    ].filter(Boolean).join(' ');
    
    const codePostal = etablissement.codePostalEtablissement !== '[ND]' ? etablissement.codePostalEtablissement : '';
    const commune = etablissement.libelleCommuneEtablissement !== '[ND]' ? etablissement.libelleCommuneEtablissement : '';
    const cityInfo = `${codePostal} ${commune}`.trim();
    
    return `${addressComponents}\n${cityInfo}`;
  };

  // Get Google Maps URL for the address
  const getGoogleMapsUrl = () => {
    if (!etablissement) return '';
    
    const addressParts = [
      [
        etablissement.numeroVoieEtablissement !== '[ND]' ? etablissement.numeroVoieEtablissement : null,
        etablissement.typeVoieEtablissement !== '[ND]' ? etablissement.typeVoieEtablissement : null,
        etablissement.libelleVoieEtablissement !== '[ND]' ? etablissement.libelleVoieEtablissement : null,
        etablissement.complementAdresseEtablissement !== '[ND]' ? etablissement.complementAdresseEtablissement : null,
      ].filter(Boolean).join(' '),
      etablissement.codePostalEtablissement !== '[ND]' ? etablissement.codePostalEtablissement : null,
      etablissement.libelleCommuneEtablissement !== '[ND]' ? etablissement.libelleCommuneEtablissement : null
    ].filter(Boolean).join(', ');
    
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressParts)}`;
  };

  // Check if company has any ND values in key fields
  const hasAnyNDValues = () => {
    if (!etablissement) return false;
    
    return [
      etablissement.numeroVoieEtablissement,
      etablissement.typeVoieEtablissement,
      etablissement.libelleVoieEtablissement,
      etablissement.complementAdresseEtablissement,
      etablissement.codePostalEtablissement,
      etablissement.libelleCommuneEtablissement,
      etablissement.denominationUsuelleEtablissement,
      etablissement.enseigne1Etablissement,
      etablissement.activitePrincipaleEtablissement,
      etablissement.trancheEffectifsEtablissement
    ].some(value => value === '[ND]');
  };

  // Check if address has ND values
  const hasNDValues = () => {
    if (!etablissement) return false;
    
    return [
      etablissement.numeroVoieEtablissement,
      etablissement.typeVoieEtablissement,
      etablissement.libelleVoieEtablissement,
      etablissement.complementAdresseEtablissement,
      etablissement.codePostalEtablissement,
      etablissement.libelleCommuneEtablissement
    ].some(value => value === '[ND]');
  };

  // Handle Google Maps click with warning
  const handleGoogleMapsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    
    if (hasNDValues()) {
      setNdWarningType('map');
      setShowNDWarning(true);
      setPendingAction(() => () => {
        window.open(getGoogleMapsUrl(), '_blank');
      });
    } else {
      window.open(getGoogleMapsUrl(), '_blank');
    }
  };

  // Handle address copy with warning
  const handleAddressCopy = () => {
    if (hasNDValues()) {
      setNdWarningType('copy');
      setShowNDWarning(true);
      setPendingAction(() => () => {
        copyToClipboard(getFormattedAddress(), 'address');
      });
    } else {
      copyToClipboard(getFormattedAddress(), 'address');
    }
  };

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-4 py-5 sm:px-6 bg-gray-50 border-b border-gray-200">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Établissement adhérent
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          Informations de l'établissement {siret}
        </p>
      </div>
      
      <div className="p-6">
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton height={24} width="50%" />
            <Skeleton count={6} />
          </div>
        ) : etablissement ? (
          <div className="space-y-6">
            {/* Identity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {etablissement.denominationUsuelleEtablissement && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Dénomination usuelle</h4>
                  <p className="mt-1 text-sm text-gray-900">
                    {etablissement.denominationUsuelleEtablissement}
                  </p>
                </div>
              )}
              
              {etablissement.enseigne1Etablissement && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Enseigne</h4>
                  <p className="mt-1 text-sm text-gray-900">
                    {etablissement.enseigne1Etablissement}
                  </p>
                </div>
              )}
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Activité principale</h4>
                <p className="mt-1 text-sm text-gray-900">
                  {etablissement.activitePrincipaleEtablissement}
                  {etablissement.nafInfo && (
                    <span className="ml-2 text-gray-600">
                      - {etablissement.nafInfo.intitule}
                    </span>
                  )}
                </p>
              </div>

              <div>
                <h4 className="text-sm font-medium text-gray-500">Date de création</h4>
                <p className="mt-1 text-sm text-gray-900">
                  {formatDate(etablissement.dateCreationEtablissement)}
                </p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Tranche d'effectifs</h4>
                <p className="mt-1 text-sm text-gray-900">
                  {getStaffSizeLabel(etablissement.trancheEffectifsEtablissement)}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 !mt-3">
              <div>
                <h4 className="text-sm font-medium text-gray-500">Statut administratif</h4>
                <p className="mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    etablissement.etatAdministratifEtablissement === 'A' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {getAdminStatusLabel(etablissement.etatAdministratifEtablissement)}
                  </span>
                </p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">Est-ce le siège social ?</h4>
                <p className="mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    etablissement.etablissementSiege === 'true' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {etablissement.etablissementSiege === 'true' ? 'Oui' : 'Non'}
                  </span>
                </p>
              </div>
              
              
              
              <div>
                <h4 className="text-sm font-medium text-gray-500">SIREN</h4>
                <div className="flex items-center mt-1">
                  <p className="text-sm font-mono">{siren}</p>
                  <button
                    onClick={() => copyToClipboard(siren, 'siren')}
                    className="ml-2 p-1 rounded-md hover:bg-gray-100 transition-colors"
                    title="Copier le SIREN"
                  >
                    <ClipboardIcon className="h-4 w-4 text-gray-500" />
                  </button>
                  {copyStatus.siren && (
                    <span className="ml-2 text-xs text-green-600">Copié !</span>
                  )}
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">SIRET</h4>
                <div className="flex items-center mt-1">
                  <p className="text-sm font-mono">{siret}</p>
                  <button
                    onClick={() => copyToClipboard(siret, 'siret')}
                    className="ml-2 p-1 rounded-md hover:bg-gray-100 transition-colors"
                    title="Copier le SIRET"
                  >
                    <ClipboardIcon className="h-4 w-4 text-gray-500" />
                  </button>
                  {copyStatus.siret && (
                    <span className="ml-2 text-xs text-green-600">Copié !</span>
                  )}
                </div>
              </div>
              
            </div>
            
            {/* Address */}
            <div>
              <h4 className="text-sm font-medium text-gray-500">Adresse</h4>
              <div className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-md border border-gray-200 relative group">
                <p>
                  {[
                    etablissement.numeroVoieEtablissement,
                    etablissement.typeVoieEtablissement,
                    etablissement.libelleVoieEtablissement,
                    etablissement.complementAdresseEtablissement,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  <br />
                  {etablissement.codePostalEtablissement}{' '}
                  {etablissement.libelleCommuneEtablissement}
                </p>
                <div className="absolute top-2 right-2 flex space-x-1">
                  <a
                    href="#"
                    onClick={handleGoogleMapsClick}
                    className="p-1.5 rounded-md hover:bg-gray-200 transition-colors text-blue-500 hover:text-blue-700"
                    title="Voir sur Google Maps"
                  >
                    <MapPinIcon className="h-4 w-4" />
                  </a>
                  <button
                    onClick={handleAddressCopy}
                    className="p-1.5 rounded-md hover:bg-gray-200 transition-colors"
                    title="Copier l'adresse"
                  >
                    <ClipboardIcon className="h-4 w-4 text-gray-500" />
                  </button>
                </div>
                {copyStatus.address && (
                  <span className="absolute top-2 right-16 text-xs text-green-600 bg-white px-1 py-0.5 rounded">Copié !</span>
                )}
              </div>
            </div>
            
            {/* Action buttons */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex flex-wrap gap-2 justify-between flex-col lg:flex-row">
                <button
                  onClick={openGoogleSearch}
                  className="flex items-center px-4 py-2 text-sm font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors"
                >
                  <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                  Rechercher sur Google
                </button>
                <button
                  onClick={openSirenPdf}
                  disabled={hasAnyNDValues()}
                  className={`flex items-center px-4 py-2 text-sm font-medium ${
                    hasAnyNDValues() 
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200' 
                      : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'
                  } rounded-md transition-colors`}
                  title={hasAnyNDValues() ? "Indisponible pour les entreprises avec données non divulguées [ND]" : "Télécharger l'avis de situation SIRENE"}
                >
                  <DocumentTextIcon className="h-4 w-4 mr-2" />
                  Avis SIREN
                </button>
                <button
                  onClick={openInpiExtrait}
                  className="flex items-center px-4 py-2 text-sm font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors"
                >
                  <DocumentTextIcon className="h-4 w-4 mr-2" />
                  Extrait INPI
                </button>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-600">Aucune information disponible pour cet établissement.</p>
        )}
      </div>

      {/* Add the warning modal */}
      <NDWarningModal 
        isOpen={showNDWarning} 
        onClose={() => setShowNDWarning(false)}
        onConfirm={() => {
          if (pendingAction) {
            pendingAction();
            setPendingAction(null);
          }
        }}
        actionType={ndWarningType}
      />
    </div>
  );
};

export default EstablishmentSection; 